import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Link } from "react-router-dom";
import { UserCog, Users, ArrowUpRight } from "lucide-react";

function initials(name = "") { return name.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase(); }

function PersonCard({ p, label, tone = "slate" }) {
  const tones = { slate: "bg-slate-50", red: "bg-red-50" };
  return (
    <Link to={`/employees/${p.id}`} className={`flex items-center gap-3 p-3 rounded-lg ${tones[tone]} hover:shadow-sm transition-all group`}>
      <Avatar className="h-11 w-11">
        <AvatarFallback className="bg-red-100 text-[#DC2626] text-sm font-semibold">{initials(p.name)}</AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        {label && <div className="text-[10px] uppercase font-semibold tracking-wider text-slate-500">{label}</div>}
        <div className="font-semibold text-sm text-slate-900 truncate group-hover:text-[#DC2626] transition-colors">{p.name}</div>
        <div className="text-xs text-slate-500 truncate">{p.designation} · {p.department}</div>
      </div>
      <ArrowUpRight className="h-4 w-4 text-slate-400 group-hover:text-[#DC2626]" strokeWidth={1.5} />
    </Link>
  );
}

export default function Team() {
  const [team, setTeam] = useState(null);
  useEffect(() => { api.get("/team/mine").then(r => setTeam(r.data)); }, []);

  if (!team) return <div className="text-slate-500">Loading…</div>;
  if (!team.me) return <div className="text-slate-500">Your account is not linked to an employee profile.</div>;

  const { me, manager, peers, reports } = team;

  return (
    <div className="space-y-6" data-testid="team-root">
      <div>
        <div className="text-xs uppercase font-semibold tracking-[0.1em] text-slate-500">My team</div>
        <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight text-slate-900">The people around you</h1>
        <p className="mt-1 text-sm text-slate-500">Your manager, your peers and the folks reporting to you.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="p-6 rounded-xl border-slate-200/60">
          <div className="flex items-center gap-2 mb-4">
            <UserCog className="h-4 w-4 text-[#DC2626]" strokeWidth={1.8} />
            <h3 className="font-display text-lg font-semibold text-slate-900">Your manager</h3>
          </div>
          {manager ? (
            <PersonCard p={manager} tone="red" />
          ) : (
            <div className="text-sm text-slate-500 py-4">You report directly to the top.</div>
          )}
        </Card>

        <Card className="p-6 rounded-xl border-slate-200/60">
          <div className="flex items-center gap-2 mb-4">
            <Users className="h-4 w-4 text-[#DC2626]" strokeWidth={1.8} />
            <h3 className="font-display text-lg font-semibold text-slate-900">Peers</h3>
            <span className="text-xs text-slate-500">({peers.length})</span>
          </div>
          {peers.length ? (
            <div className="space-y-2">
              {peers.map(p => <PersonCard key={p.id} p={p} />)}
            </div>
          ) : <div className="text-sm text-slate-500 py-4">No peers in your reporting line.</div>}
        </Card>

        <Card className="p-6 rounded-xl border-slate-200/60">
          <div className="flex items-center gap-2 mb-4">
            <Users className="h-4 w-4 text-emerald-600" strokeWidth={1.8} />
            <h3 className="font-display text-lg font-semibold text-slate-900">Direct reports</h3>
            <span className="text-xs text-slate-500">({reports.length})</span>
          </div>
          {reports.length ? (
            <div className="space-y-2">
              {reports.map(p => <PersonCard key={p.id} p={p} />)}
            </div>
          ) : <div className="text-sm text-slate-500 py-4">You don't have direct reports yet.</div>}
        </Card>
      </div>
    </div>
  );
}
